import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DeviceSelector } from './DeviceSelectors';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { useToast } from '../ui/ToastProvider';
import { deviceService } from '../../services/device.service';
import { repairService, type CreateRepairData } from '../../services/repair.service';
import { addressService } from '../../services/address.service';
import { useAuth } from '../../hooks/useAuth';
import { openAuthModal } from '../../lib/authModalEvents';
import { Loader2, ChevronLeft, ChevronRight, Upload, X } from 'lucide-react';

const repairSchema = z.object({
  faults: z.array(z.string()).optional(),
  description: z.string().optional(),
  devicePassword: z.string().optional(),
  imei: z.string().optional(),
  serialNumber: z.string().optional(),
  addressId: z.number().optional(),
  pickupRequired: z.boolean().optional(),
  preferredDate: z.string().optional(),
  notes: z.string().optional(),
});

type RepairFormData = z.infer<typeof repairSchema>;

const steps = ['Device Selection', 'Fault Details', 'Review & Submit'];

export const RepairBookingForm: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [deviceSelection, setDeviceSelection] = useState<{
    categoryId: number;
    brandId: number;
    modelId: number;
    customModelName?: string;
  } | null>(null);
  const [customFaultName, setCustomFaultName] = useState('');
  const [isCustomFaultSelected, setIsCustomFaultSelected] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RepairFormData>({
    resolver: zodResolver(repairSchema),
    defaultValues: {
      faults: [],
      pickupRequired: false,
    },
  });

  const faults = watch('faults');
  const pickupRequired = watch('pickupRequired');

  // Fetch faults for selection
  const { data: faultOptions, isLoading: faultsLoading, isError: faultsError, refetch: refetchFaults } = useQuery({
    queryKey: ['faults', deviceSelection?.categoryId],
    queryFn: () => deviceService.getFaults(deviceSelection?.categoryId),
    enabled: !!deviceSelection,
  });

  // Fetch user's addresses (only for logged-in users; guests don't need them)
  const { data: addresses } = useQuery({
    queryKey: ['addresses'],
    queryFn: addressService.getAll,
    enabled: !!user,
  });

  const createRepair = useMutation({
    mutationFn: repairService.create,
    onSuccess: (data) => {
      showToast('Repair request submitted successfully!', 'success');
      navigate(`/dashboard/repairs/${data.id}`);
    },
    onError: () => {
      showToast('Failed to submit repair request', 'error');
    },
  });

  const handleDeviceSelect = useCallback((selection: {
    categoryId: number;
    brandId: number;
    modelId: number;
    customModelName?: string;
  }) => {
    setDeviceSelection(selection);
  }, []);

  const handleFaultToggle = (faultId: string) => {
    const current = faults || [];
    if (current.includes(faultId)) {
      setValue('faults', current.filter((f) => f !== faultId));
    } else {
      setValue('faults', [...current, faultId]);
    }
  };

  const selectedFaultCount = (() => {
    const selected = faults || [];
    if (isCustomFaultSelected && customFaultName.trim()) {
      return selected.includes(customFaultName.trim()) ? selected.length : selected.length + 1;
    }
    return selected.length;
  })();

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) {
      showToast('Maximum 5 photos allowed', 'error');
      return;
    }
    setPhotos([...photos, ...files]);
    const previews = files.map((file) => URL.createObjectURL(file));
    setPhotoPreviews([...photoPreviews, ...previews]);
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    if (currentStep === 0 && !deviceSelection) {
      showToast('Please select your device', 'error');
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const onSubmit = async (data: RepairFormData) => {
    if (!deviceSelection) return;

    if (!user) {
      showToast('Please log in to submit your repair request', 'error');
      openAuthModal('login');
      return;
    }

    let modelId = deviceSelection.modelId;
    if (deviceSelection.customModelName) {
      const customModel = await deviceService.createModel(deviceSelection.brandId, deviceSelection.customModelName);
      modelId = customModel.id;
    }

    const photoUrls: string[] = [];
    const clean = (v?: string) => (v ? v.trim() || undefined : undefined);

    const normalizedFaults = new Set((data.faults ?? []).filter(Boolean));
    const trimmedCustomFault = customFaultName.trim();
    if (isCustomFaultSelected && trimmedCustomFault) {
      normalizedFaults.add(trimmedCustomFault);
    }

    const payload: CreateRepairData = {
      deviceCategoryId: deviceSelection.categoryId,
      deviceBrandId: deviceSelection.brandId,
      deviceModelId: modelId,
      devicePassword: clean(data.devicePassword),
      imei: clean(data.imei),
      serialNumber: clean(data.serialNumber),
      faults: Array.from(normalizedFaults),
      description: clean(data.description),
      photos: photoUrls,
      addressId: data.addressId || undefined,
      pickupRequired: !!data.pickupRequired,
      preferredDate: clean(data.preferredDate),
      notes: clean(data.notes),
    };

    createRepair.mutate(payload);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Select Your Device</h3>
            <DeviceSelector onSelect={handleDeviceSelect} />
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Fault Details</h3>

            {/* Fault Selection */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                What's wrong with your device? <span className="text-red-500">*</span>
              </label>
              {faultsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : faultsError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                  <p className="text-sm text-red-600">We couldn't load the fault options.</p>
                  <button
                    type="button"
                    onClick={() => refetchFaults()}
                    className="mt-2 text-sm font-medium text-primary hover:underline"
                  >
                    Try again
                  </button>
                </div>
              ) : faultOptions && faultOptions.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {faultOptions.map((fault) => (
                      <button
                        key={fault.id}
                        type="button"
                        onClick={() => handleFaultToggle(fault.slug)}
                        className={`rounded-lg border p-3 text-sm transition-all ${
                          faults?.includes(fault.slug)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-[rgb(var(--border))] text-[rgb(var(--foreground))] hover:border-primary/50'
                        }`}
                      >
                        {fault.name}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomFaultSelected((prev) => !prev);
                      if (isCustomFaultSelected) {
                        setCustomFaultName('');
                      }
                    }}
                    className={`w-full rounded-lg border border-dashed p-3 text-sm transition-all ${
                      isCustomFaultSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-[rgb(var(--border))] text-[rgb(var(--light-secondary))] hover:border-primary/50'
                    }`}
                  >
                    My fault isn’t listed
                  </button>

                  {isCustomFaultSelected && (
                    <div className="rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-4">
                      <label className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]">
                        Enter your issue
                      </label>
                      <input
                        type="text"
                        value={customFaultName}
                        onChange={(event) => setCustomFaultName(event.target.value)}
                        placeholder="e.g. Water damage, charging port issue, speaker not working"
                        className="w-full rounded-lg border border-[rgb(var(--border))] bg-transparent px-3 py-2 text-sm outline-none ring-0 transition focus:border-primary"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-4 text-center">
                  <p className="text-sm text-[rgb(var(--light-secondary))]">
                    No fault options are available for this device, but you can still
                    describe the problem below.
                  </p>
                  <div className="mt-4 rounded-lg border border-dashed border-[rgb(var(--border))] bg-transparent p-4 text-left">
                    <label className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]">
                      Describe the issue
                    </label>
                    <input
                      type="text"
                      value={customFaultName}
                      onChange={(event) => setCustomFaultName(event.target.value)}
                      placeholder="e.g. Screen flickering, battery swelling"
                      className="w-full rounded-lg border border-[rgb(var(--border))] bg-transparent px-3 py-2 text-sm outline-none ring-0 transition focus:border-primary"
                    />
                  </div>
                </div>
              )}
              {errors.faults && (
                <p className="mt-1 text-sm text-red-500">{errors.faults.message}</p>
              )}
            </div>

            {/* Additional Description */}
            <Input
              label="Additional Description (Optional)"
              placeholder="Describe any other issues or details..."
              {...register('description')}
            />

            {/* Device Info */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Device Password/PIN (Optional)"
                placeholder="If applicable, for diagnostics"
                type="password"
                {...register('devicePassword')}
              />
              <Input
                label="IMEI Number (Optional)"
                placeholder="15-digit IMEI"
                {...register('imei')}
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label className="mb-2 block text-sm font-medium">Photos (Optional, max 5)</label>
              <div className="flex flex-wrap gap-4">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="relative h-20 w-20">
                    <img
                      src={preview}
                      alt={`Device photo ${index + 1}`}
                      className="h-full w-full rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {photos.length < 5 && (
                  <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-[rgb(var(--border))] hover:border-primary/50">
                    <Upload className="h-6 w-6 text-[rgb(var(--light-secondary))]" />
                    <span className="text-xs text-[rgb(var(--light-secondary))]">Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Pickup & Address */}
            <div className="space-y-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('pickupRequired')}
                  className="rounded border-[rgb(var(--border))]"
                />
                <span className="text-sm">I need pickup/delivery service</span>
              </label>

              {pickupRequired && (
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Delivery Address
                  </label>
                  <select
                    {...register('addressId', { valueAsNumber: true })}
                    className="w-full rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] px-4 py-3 text-[rgb(var(--foreground))] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select an address</option>
                    {addresses?.map((address) => (
                      <option key={address.id} value={address.id}>
                        {address.label} - {address.street}, {address.city}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <Input
                label="Preferred Date (Optional)"
                type="date"
                {...register('preferredDate')}
              />
            </div>

            <Input
              label="Additional Notes (Optional)"
              placeholder="Any other information for the technician..."
              {...register('notes')}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Review Your Repair Request</h3>

            <div className="rounded-lg bg-[rgb(var(--card))] border border-[rgb(var(--border))] p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Device</span>
                <span className="font-medium">
                  {deviceSelection?.categoryId && deviceSelection?.brandId && deviceSelection?.modelId
                    ? `${faultOptions?.[0]?.name || 'Device'}`
                    : 'Selected'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[rgb(var(--light-secondary))]">Faults</span>
                <span className="font-medium">{selectedFaultCount} selected</span>
              </div>
              {pickupRequired && (
                <div className="flex justify-between">
                  <span className="text-[rgb(var(--light-secondary))]">Pickup Required</span>
                  <span className="font-medium">Yes</span>
                </div>
              )}
              {watch('preferredDate') && (
                <div className="flex justify-between">
                  <span className="text-[rgb(var(--light-secondary))]">Preferred Date</span>
                  <span className="font-medium">{watch('preferredDate')}</span>
                </div>
              )}
              {watch('description') && (
                <div>
                  <span className="text-[rgb(var(--light-secondary))]">Description</span>
                  <p className="mt-1 text-sm">{watch('description')}</p>
                </div>
              )}
            </div>

            <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
              <p className="text-sm text-[rgb(var(--light-secondary))]">
                Our team will review your request and provide an estimate within 24 hours.
                You'll be notified via email and SMS.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          {steps.map((_, index) => (
            <div key={index} className="flex items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  index <= currentStep
                    ? 'bg-primary text-white'
                    : 'bg-[rgb(var(--border))] text-[rgb(var(--light-secondary))]'
                }`}
              >
                {index + 1}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`h-0.5 w-8 sm:w-16 ${
                    index < currentStep ? 'bg-primary' : 'bg-[rgb(var(--border))]'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-xs text-[rgb(var(--light-secondary))]">
          {steps.map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </div>
      </div>

      {renderStep()}

      <div className="mt-8 flex justify-between border-t border-[rgb(var(--border))] pt-6">
        <Button
          type="button"
          variant="secondary"
          onClick={prevStep}
          disabled={currentStep === 0}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        {currentStep < steps.length - 1 ? (
          <Button type="button" variant="primary" onClick={nextStep}>
            Next
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            type="submit"
            variant="primary"
            disabled={createRepair.isPending}
          >
            {createRepair.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Repair Request'
            )}
          </Button>
        )}
      </div>
    </form>
  );
};